import * as Yup from 'yup';
import { useEffect, useRef } from 'react';
import { useAppSelector } from '@/shared/hooks/store';
import { resetLoading } from '@/store/slices/auth/selectors';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';

// Components
import { Input } from '@/shared/ui';
import Button from '@/shared/ui/button';
import { Formik } from 'formik';
import SecurityIcon from '@/assets/icons/security.svg';

interface Props {
    handleChangeStep: (val: string) => void;
    initialEmail: string;
}

const validationSchema = Yup.object().shape({
    email: Yup.string().email(ERROR_MESSAGES.VALID_EMAIL).required(ERROR_MESSAGES.REQUIRED_FIELD_MISSING)
});

const ForgotPassword = ({ handleChangeStep, initialEmail }: Props) => {
    const loading = useAppSelector(resetLoading);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            const inp = inputRef.current.querySelector('input');
            if (inp) inp.focus();
        }
    }, []);

    return (
        <div className="flex w-full flex-col gap-7" data-testid="forgot-password-container">
            <div className="flex w-full flex-col items-center justify-center gap-3">
                <img src={SecurityIcon} alt="Security icon" className="h-[35px] w-[26px] md:h-[57px] md:w-[44px]" />
                <h2 className="py-1 font-agrandir text-2xl font-normal md:text-4xl" data-testid="forgot-password-title">Forgot Password</h2>
                <p className="text-center font-dmSans text-17 text-black md:text-xl" data-testid="forgot-password-content-text">
                    Please enter the email associated with this account so we can verify your account.
                </p>
            </div>

            <div className="flex flex-col gap-4">
                <Formik
                    initialValues={{ email: initialEmail }}
                    validationSchema={validationSchema}
                    onSubmit={(values): void => {
                        handleChangeStep(values.email);
                    }}
                >
                    {({ values, errors, touched, handleSubmit, setFieldValue }) => (
                        <div className="flex flex-col gap-6">
                            <Input
                                name="email"
                                label="Email"
                                value={values.email}
                                required
                                autoFocus
                                placeholder="ex. janed@gmail.com"
                                error={!!(touched.email && errors.email)}
                                errorMessage={errors.email}
                                onChange={event => setFieldValue('email', event.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                                testId="forgot-email"
                            />

                            <div className="flex w-full flex-col items-start gap-4">
                                <Button
                                    sx={{ width: '100%' }}
                                    disabled={loading}
                                    onClick={() => handleSubmit()}
                                    fontClassName="text-base md-max:py-0.5"
                                    showEndIcon
                                    testId="button-send-verification"
                                >
                                    Send Verification Code
                                </Button>
                            </div>
                        </div>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default ForgotPassword;
